
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Edit, Trash2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/hooks/use-user';

interface StoreProduct {
  id: string;
  title: string;
  description: string;
  price: number;
  discount_price?: number;
  images: string[];
  design_image_url?: string;
  store_name: string;
  category: string;
  available_colors: string[];
  available_sizes: string[];
  print_options: string[];
  mockup_images: Record<string, Record<string, string>>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ProductsListProps {
  storeId: string;
}

const ProductsList: React.FC<ProductsListProps> = ({ storeId }) => {
  const { user } = useUser();
  const [selectedProduct, setSelectedProduct] = useState<StoreProduct | null>(null);

  const { data: products, isLoading, refetch } = useQuery({
    queryKey: ['store-products', storeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('store_products')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching products:', error);
        throw error;
      }

      return data as StoreProduct[];
    },
    enabled: !!user,
  });

  const handleDeleteProduct = async (productId: string, productTitle: string) => {
    if (!confirm(`Tem certeza que deseja excluir o produto "${productTitle}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('store_products')
        .delete()
        .eq('id', productId);

      if (error) {
        console.error('Error deleting product:', error);
        toast.error('Erro ao excluir produto');
        return;
      }

      toast.success('Produto excluído com sucesso!');
      refetch();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erro ao excluir produto');
    }
  };

  const handleToggleActive = async (productId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('store_products')
        .update({ is_active: !currentStatus })
        .eq('id', productId);

      if (error) {
        console.error('Error updating product:', error);
        toast.error('Erro ao atualizar status do produto');
        return;
      }

      toast.success(`Produto ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      refetch();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Erro ao atualizar produto');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhum produto criado ainda</h3>
          <p className="text-muted-foreground mb-4">
            Vá para o Editor, crie um design e gere a imagem combinada para criar seus primeiros produtos
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Produtos da Loja ({products.length})</h3>
      </div>

      <div className="grid gap-4">
        {products.map((product) => (
          <Card key={product.id} className="overflow-hidden">
            <div className="flex">
              {/* Imagem do Produto */}
              <div className="w-32 h-32 flex-shrink-0">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <Package className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Informações do Produto */}
              <div className="flex-1 p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-lg">{product.title}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {product.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={product.is_active ? "default" : "secondary"}>
                      {product.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                    <Badge variant="outline" className="capitalize">
                      {product.category}
                    </Badge>
                  </div>
                </div>

                {/* Preço */}
                <div className="mb-3">
                  {product.discount_price ? (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-green-600">
                        R$ {product.discount_price.toFixed(2)}
                      </span>
                      <span className="text-sm text-muted-foreground line-through">
                        R$ {product.price.toFixed(2)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-lg font-bold text-green-600">
                      R$ {product.price.toFixed(2)}
                    </span>
                  )}
                </div>

                {/* Cores Disponíveis */}
                <div className="mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Cores: </span>
                  <div className="inline-flex gap-1 flex-wrap">
                    {product.available_colors.map((color, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {color}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Tamanhos Disponíveis */}
                <div className="mb-3">
                  <span className="text-sm font-medium text-muted-foreground">Tamanhos: </span>
                  <div className="inline-flex gap-1 flex-wrap">
                    {product.available_sizes.map((size, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {size}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Data de Criação */}
                <div className="text-xs text-muted-foreground mb-3">
                  Criado em: {new Date(product.created_at).toLocaleDateString('pt-BR')}
                </div>

                {/* Ações */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Visualizar
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleActive(product.id, product.is_active)}
                  >
                    {product.is_active ? 'Desativar' : 'Ativar'}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDeleteProduct(product.id, product.title)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Excluir
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Modal de Visualização */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">{selectedProduct.title}</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedProduct(null)}
                >
                  ✕
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Imagens */}
                <div>
                  {selectedProduct.images && selectedProduct.images.length > 0 && (
                    <img
                      src={selectedProduct.images[0]}
                      alt={selectedProduct.title}
                      className="w-full h-64 object-contain bg-gray-100 rounded-lg"
                    />
                  )}
                </div>

                {/* Detalhes */}
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium">Descrição:</h4>
                    <p className="text-sm text-muted-foreground">{selectedProduct.description}</p>
                  </div>

                  <div>
                    <h4 className="font-medium">Preço:</h4>
                    <span className="text-lg font-bold text-green-600">
                      R$ {selectedProduct.price.toFixed(2)}
                    </span>
                  </div>

                  <div>
                    <h4 className="font-medium">Categoria:</h4>
                    <Badge variant="outline" className="capitalize">
                      {selectedProduct.category}
                    </Badge>
                  </div>

                  <div>
                    <h4 className="font-medium">Status:</h4>
                    <Badge variant={selectedProduct.is_active ? "default" : "secondary"}>
                      {selectedProduct.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>

                  <div>
                    <h4 className="font-medium">Cores Disponíveis:</h4>
                    <div className="flex gap-1 flex-wrap">
                      {selectedProduct.available_colors.map((color, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {color}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium">Tamanhos Disponíveis:</h4>
                    <div className="flex gap-1 flex-wrap">
                      {selectedProduct.available_sizes.map((size, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {size}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsList;
